import { supabase } from '@/integrations/supabase/client'

export interface RdwVehicleData {
  brand: string
  model: string
  licensePlate: string
  year: number | null
  fuelType: string
  color: string
  displacementCc: number | null
  cylinderCount: number | null
  powerKw: number | null
  powerHp: number | null
  isHybrid: boolean
  combinedPowerKw: number | null
  combinedPowerHp: number | null
}

export interface EngineVariant {
  id: string
  engineCode: string | null
  engineName: string
  fuelType: string
  displacementCc: number | null
  cylinderCount: number | null
  turbo: boolean
  yearFrom: number | null
  yearTo: number | null
  originalHp: number
  originalKw: number | null
  originalNm: number | null
  brandName: string
  modelName: string
}

export interface ChiptuningStage {
  id: string
  stage: string
  tunedHp: number
  tunedKw: number | null
  tunedNm: number | null
  hpGain: number
  nmGain: number
  price: number | null
  description: string | null
  features: string[]
  available: boolean
}

export interface ChiptuningResult {
  vehicle: RdwVehicleData
  engineVariants: EngineVariant[]
  selectedVariant: EngineVariant | null
  stages: ChiptuningStage[]
}

const BRAND_ALIASES: Record<string, string> = {
  'VOLKSWAGEN': 'Volkswagen',
  'VW': 'Volkswagen',
  'AUDI': 'Audi',
  'BMW': 'BMW',
  'MERCEDES-BENZ': 'Mercedes-Benz',
  'MERCEDES': 'Mercedes-Benz',
  'PORSCHE': 'Porsche',
  'TOYOTA': 'Toyota',
  'FORD': 'Ford',
  'OPEL': 'Opel',
  'RENAULT': 'Renault',
  'PEUGEOT': 'Peugeot',
  'CITROEN': 'Citroën',
  'CITROËN': 'Citroën',
  'VOLVO': 'Volvo',
  'SEAT': 'Seat',
  'SKODA': 'Škoda',
  'ŠKODA': 'Škoda',
  'MINI': 'Mini',
  'FIAT': 'Fiat',
  'ALFA ROMEO': 'Alfa Romeo',
  'LAND ROVER': 'Land Rover',
  'JAGUAR': 'Jaguar',
  'MASERATI': 'Maserati',
  'LAMBORGHINI': 'Lamborghini',
  'FERRARI': 'Ferrari',
  'BENTLEY': 'Bentley',
  'ROLLS-ROYCE': 'Rolls-Royce',
  'ASTON MARTIN': 'Aston Martin',
  'MCLAREN': 'McLaren',
  'TESLA': 'Tesla',
  'KIA': 'Kia',
  'HYUNDAI': 'Hyundai',
  'MAZDA': 'Mazda',
  'NISSAN': 'Nissan',
  'HONDA': 'Honda',
  'SUZUKI': 'Suzuki',
  'MITSUBISHI': 'Mitsubishi',
  'LEXUS': 'Lexus',
  'INFINITI': 'Infiniti',
  'CUPRA': 'Cupra',
  'DS': 'DS',
  'DODGE': 'Dodge',
  'CHEVROLET': 'Chevrolet',
  'JEEP': 'Jeep',
  'CHRYSLER': 'Chrysler',
}

function normalizeBrand(rdwBrand: string): string {
  const upper = rdwBrand.toUpperCase().trim()
  return BRAND_ALIASES[upper] || rdwBrand.charAt(0).toUpperCase() + rdwBrand.slice(1).toLowerCase()
}

function kwToHp(kw: number): number {
  return Math.round(kw * 1.36)
}



// ─── RDW DATA OPHALEN ─────────────────────────────────────────

export async function fetchRdwData(licensePlate: string): Promise<RdwVehicleData> {
  const clean = licensePlate.replace(/-/g, '').toUpperCase()

  const [vehicleRes, fuelRes] = await Promise.all([
    fetch(`https://opendata.rdw.nl/resource/m9d7-ebf2.json?kenteken=${clean}`),
    fetch(`https://opendata.rdw.nl/resource/8ys7-d773.json?kenteken=${clean}`),
  ])

  const vehicles = await vehicleRes.json()
  const fuels = await fuelRes.json()
  const vehicleInfo = vehicles[0]
  const iceFuel = fuels.find((f: any) => f.brandstof_omschrijving !== 'Elektriciteit') || fuels[0]
  const electricFuel = fuels.find((f: any) => f.brandstof_omschrijving === 'Elektriciteit')

  if (!vehicleInfo) {
    throw new Error('Voertuig niet gevonden bij de RDW')
  }

  const isHybrid = !!electricFuel?.klasse_hybride_elektrisch_voertuig ||
    (vehicleInfo.handelsbenaming || '').toUpperCase().includes('HYBRID')

  const iceKw = iceFuel?.nettomaximumvermogen ? parseFloat(iceFuel.nettomaximumvermogen) : null
  const electricKw = electricFuel?.netto_max_vermogen_elektrisch
    ? parseFloat(electricFuel.netto_max_vermogen_elektrisch)
    : null

  // Voor hybrides: ICE + elektrisch vermogen = gecombineerd systeemvermogen
  // RDW rapporteert alleen het ICE-vermogen apart
  let combinedKw: number | null = null
  if (isHybrid && iceKw && electricKw) {
    combinedKw = iceKw + electricKw
  }

  const displacementCc = vehicleInfo.cilinderinhoud ? parseInt(vehicleInfo.cilinderinhoud) : null
  const cylinderCount = vehicleInfo.aantal_cilinders ? parseInt(vehicleInfo.aantal_cilinders) : null

  return {
    brand: vehicleInfo.merk || 'Onbekend',
    model: vehicleInfo.handelsbenaming || 'Onbekend',
    licensePlate,
    year: vehicleInfo.datum_eerste_toelating
      ? parseInt(vehicleInfo.datum_eerste_toelating.substring(0, 4))
      : null,
    fuelType: iceFuel?.brandstof_omschrijving || 'Onbekend',
    color: vehicleInfo.eerste_kleur || 'Onbekend',
    displacementCc,
    cylinderCount,
    powerKw: iceKw,
    powerHp: iceKw ? kwToHp(iceKw) : null,
    isHybrid,
    combinedPowerKw: combinedKw,
    combinedPowerHp: combinedKw ? kwToHp(combinedKw) : null,
  }
}


// ─── BESTAANDE VARIANTEN ZOEKEN ───────────────────────────────

export async function findEngineVariants(vehicle: RdwVehicleData): Promise<EngineVariant[]> {
  const brand = normalizeBrand(vehicle.brand)

  const { data, error } = await (supabase as any)
    .from('engine_variants')
    .select(`
      *,
      vehicle_models!inner(
        name,
        vehicle_brands!inner(name)
      )
    `)
    .eq('vehicle_models.vehicle_brands.name', brand)

  if (error) {
    console.error('Error finding engine variants:', error)
    return []
  }

  const rdwModel = vehicle.model.toUpperCase()
  const rdwFuel = vehicle.fuelType.toUpperCase()

  const mapped: EngineVariant[] = (data || []).map((item: any) => ({
    id: item.id,
    engineCode: item.engine_code,
    engineName: item.engine_name,
    fuelType: item.fuel_type,
    displacementCc: item.displacement_cc,
    cylinderCount: item.cylinder_count,
    turbo: item.turbo,
    yearFrom: item.year_from,
    yearTo: item.year_to,
    originalHp: item.original_hp,
    originalKw: item.original_kw,
    originalNm: item.original_nm,
    brandName: item.vehicle_models.vehicle_brands.name,
    modelName: item.vehicle_models.name,
  }))

  // Normaliseer RDW modelnaam: verwijder aandrijf-suffixen zodat alleen
  // het carrosserie-model overblijft (bijv. "CAYENNE E-HYBRID" → "CAYENNE")
  const STRIP_SUFFIXES = /\b(E-?HYBRID|HYBRID|PHEV|PLUG-?IN|MHEV|EV|TFSI|TSI|TDI|CDI|BLUE\s*HDI|GTI|GTD)\b/gi
  const rdwModelClean = rdwModel.replace(STRIP_SUFFIXES, '').replace(/\s+/g, ' ').trim()

  // Multi-word sub-varianten die als suffix achter het basismodel staan
  // Langere patronen eerst zodat "SPORT TURISMO" voor "SPORT" gematcht wordt
  const SUB_VARIANT_PATTERNS = [
    'GRAN COUPE', 'GRAN COUPÉ', 'SPORT TURISMO', 'SHOOTING BRAKE',
    'COUPE', 'COUPÉ', 'SPORTBACK', 'AVANT', 'CABRIOLET', 'CABRIO',
    'ROADSTER', 'SPIDER', 'SPYDER', 'TARGA', 'TOURING',
  ]

  function detectSubVariant(modelName: string): string | null {
    const upper = modelName.toUpperCase().replace(/É/g, 'E')
    for (const sv of SUB_VARIANT_PATTERNS) {
      const svNorm = sv.replace(/É/g, 'E')
      // Match als hele woorden, niet als onderdeel van een ander woord
      const regex = new RegExp(`\\b${svNorm}\\b`)
      if (regex.test(upper)) return svNorm
    }
    return null
  }

  const rdwSubVariant = detectSubVariant(rdwModelClean)

  const scored = mapped.map(variant => {
    let score = 0
    const dbModel = variant.modelName

    // Normaliseer voor vergelijking
    const dbUpper = dbModel.toUpperCase().replace(/É/g, 'E')
    const rdwUpper = rdwModelClean.toUpperCase().replace(/É/g, 'E')

    // Verwijder spaties/streepjes voor compacte vergelijking (bijv. "RS 6" → "RS6")
    const rdwCompact = rdwUpper.replace(/[\s-]+/g, '')
    const dbCompact = dbUpper.replace(/[\s-]+/g, '')

    // Haal basismodel op: sub-variant verwijderd, compact gemaakt
    const rdwWithoutSub = rdwSubVariant
      ? rdwUpper.replace(new RegExp(`\\b${rdwSubVariant}\\b`), '').trim()
      : rdwUpper
    const dbWithoutSub = detectSubVariant(dbModel)
      ? dbUpper.replace(new RegExp(`\\b${detectSubVariant(dbModel)}\\b`), '').trim()
      : dbUpper
    const rdwBaseCompact = rdwWithoutSub.replace(/[\s-]+/g, '')
    const dbBaseCompact = dbWithoutSub.replace(/[\s-]+/g, '')

    // Basismodel moet overeenkomen (spatie-onafhankelijk)
    const baseMatches = rdwBaseCompact === dbBaseCompact ||
      rdwCompact.includes(dbCompact) ||
      dbCompact.includes(rdwBaseCompact)

    if (!baseMatches) return { variant, score: 0 }

    // Sub-variant matching: "Cayenne" mag niet matchen met "Cayenne Coupé"
    const dbSubVariant = detectSubVariant(dbModel)

    if (rdwSubVariant && dbSubVariant) {
      if (rdwSubVariant !== dbSubVariant) return { variant, score: 0 }
      score += 20
    } else if (!rdwSubVariant && !dbSubVariant) {
      score += 20
    } else {
      score += 5
    }

    // Year matching — prefer variants from the right generation
    if (vehicle.year && variant.yearFrom) {
      const yearTo = variant.yearTo || new Date().getFullYear() + 2
      if (vehicle.year >= variant.yearFrom && vehicle.year <= yearTo) {
        score += 10
      } else {
        const gap = Math.min(
          Math.abs(vehicle.year - variant.yearFrom),
          Math.abs(vehicle.year - yearTo),
        )
        if (gap > 10) return { variant, score: 0 }
        score -= gap
      }
    }

    // Fuel type MUST match
    const fuelMatch =
      (rdwFuel.includes('BENZINE') && variant.fuelType === 'Benzine') ||
      (rdwFuel.includes('DIESEL') && variant.fuelType === 'Diesel')
    if (rdwFuel && variant.fuelType && !fuelMatch) {
      return { variant, score: 0 }
    }
    if (fuelMatch) score += 5

    // Displacement MUST be close (max 500cc difference)
    if (vehicle.displacementCc && variant.displacementCc) {
      const diff = Math.abs(vehicle.displacementCc - variant.displacementCc)
      if (diff > 500) return { variant, score: 0 }
      if (diff <= 50) score += 8
      else if (diff <= 150) score += 4
      else if (diff <= 300) score += 2
    }

    // Power matching — hybrides vergelijken op zowel ICE als gecombineerd vermogen
    if (vehicle.powerHp && variant.originalHp) {
      const iceDiff = Math.abs(vehicle.powerHp - variant.originalHp)
      const combinedDiff = vehicle.combinedPowerHp
        ? Math.abs(vehicle.combinedPowerHp - variant.originalHp)
        : Infinity

      const isHybridVariant = variant.engineName.toLowerCase().includes('hybrid') ||
        variant.engineName.toLowerCase().includes('e-hybrid')

      if (vehicle.isHybrid && isHybridVariant && vehicle.combinedPowerHp) {
        // Hybride auto + hybride variant: match op gecombineerd vermogen
        const maxDiff = Math.max(vehicle.combinedPowerHp * 0.15, 30)
        if (combinedDiff > maxDiff) return { variant, score: 0 }
        if (combinedDiff <= 15) score += 12
        else if (combinedDiff <= 40) score += 8
        else score += 4
      } else if (vehicle.isHybrid && isHybridVariant) {
        // Hybride auto + hybride variant maar geen gecombineerd vermogen: mildere matching
        const maxDiff = Math.max(variant.originalHp * 0.40, 50)
        if (iceDiff > maxDiff) return { variant, score: 0 }
        score += 4
      } else if (vehicle.isHybrid && !isHybridVariant) {
        // Hybride auto + niet-hybride variant: alleen matchen op ICE vermogen, lagere score
        const maxDiff = Math.max(vehicle.powerHp * 0.15, 20)
        if (iceDiff > maxDiff) return { variant, score: 0 }
        if (iceDiff <= 10) score += 4
        else if (iceDiff <= 30) score += 2
        else score += 1
      } else {
        // Normale auto: standaard matching
        const maxDiff = Math.max(vehicle.powerHp * 0.15, 20)
        if (iceDiff > maxDiff) return { variant, score: 0 }
        if (iceDiff <= 10) score += 8
        else if (iceDiff <= 30) score += 4
        else score += 2
      }
    }

    return { variant, score }
  })

  const sorted = scored
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .map(s => s.variant)

  // Deduplicate: als dezelfde motor (engine_name + HP) voorkomt bij meerdere
  // sub-modellen (bijv. Cayenne vs Cayenne Coupé), houd alleen de best scorende
  const seen = new Set<string>()
  return sorted.filter(v => {
    const key = `${v.engineName}|${v.originalHp}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

// ─── CHIPTUNING STAGES OPHALEN ────────────────────────────────

export async function getChiptuningStages(engineVariantId: string): Promise<ChiptuningStage[]> {
  const { data, error } = await (supabase as any)
    .from('chiptuning_data')
    .select('*')
    .eq('engine_variant_id', engineVariantId)
    .eq('available', true)
    .order('stage', { ascending: true })

  if (error) {
    console.error('Error fetching chiptuning stages:', error)
    return []
  }

  return (data || []).map((item: any) => ({
    id: item.id,
    stage: item.stage,
    tunedHp: item.tuned_hp,
    tunedKw: item.tuned_kw,
    tunedNm: item.tuned_nm,
    hpGain: item.hp_gain,
    nmGain: item.nm_gain,
    price: item.price ? parseFloat(item.price) : null,
    description: item.description,
    features: item.features || [],
    available: item.available,
  }))
}


// ─── HOOFD LOOKUP FUNCTIE ─────────────────────────────────────

export async function lookupChiptuning(licensePlate: string): Promise<ChiptuningResult> {
  const vehicle = await fetchRdwData(licensePlate)
  const engineVariants = await findEngineVariants(vehicle)

  let selectedVariant: EngineVariant | null = null
  let stages: ChiptuningStage[] = []

  if (engineVariants.length > 0) {
    selectedVariant = engineVariants[0]
    stages = await getChiptuningStages(selectedVariant.id)
  }

  return { vehicle, engineVariants, selectedVariant, stages }
}
